/** The Insights page's top bar for a tab (item 71): the view switch, the tab's
 *  actions and its filters stay at the top while the page scrolls - sticky
 *  under the page header on every tab that has them.
 *
 *  - Its layout never changes when it sticks: only a surface, a hairline and a
 *    soft shadow appear (box-shadow, no border width), so nothing jumps.
 *  - It knows it is stuck from itself: it sticks one pixel above the scroll
 *    area's edge, and a one-pixel line along its own top (inside its padding,
 *    so its overhanging sides never count) is then clipped away.
 *    IntersectionObserver clips through every scrolling ancestor (as the agent
 *    gallery's tab bar relies on), so no root and no scroll listener - and
 *    nothing beside it that a grid could place apart.
 *  - On a wider screen a tab that gives its filters as a function puts its
 *    view switch and actions INSIDE the filter row (item 86: a row holding only
 *    "New template" or "Back" was dead space).
 *  - On a phone it is one row: the view switch and a "Filters" button with how
 *    many are set; the filters open under it, inside the bar.
 *  - It stacks locally (a plain number inside the page): under the app's
 *    chrome, the bell and every overlay; dropdowns and date pickers open in
 *    their own popup layer above it. */
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { FilterOutlined, UpOutlined } from '@ant-design/icons';
import { useLanguage } from '@/hooks/useLanguage';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import './InsightsStickyBar.css';

/** What a tab puts in its filter row: its view switch at the start, its actions at the end. */
export interface FilterSlots { start?: ReactNode; end?: ReactNode }

interface Props {
  /** The tab's view switch (Dashboard / One at a time, Library / Report templates). */
  views?: ReactNode;
  /** The tab's actions (New template, Run a report now…). */
  actions?: ReactNode;
  /** The tab's filters (organizations, agents, dates…) - or a row that takes the tab's views and actions in it. */
  filters?: ReactNode | ((slots: FilterSlots) => ReactNode);
  /** How many filters differ from their defaults (shown on the phone's Filters button). */
  filterCount?: number;
  testId?: string;
}

export function InsightsStickyBar({ views, actions, filters, filterCount = 0, testId }: Props) {
  const { t } = useLanguage();
  const bar = useRef<HTMLDivElement | null>(null);
  const edge = useRef<HTMLSpanElement | null>(null);
  const [stuck, setStuck] = useState(false);
  const phone = useMediaQuery('(max-width: 600px)');
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const el = edge.current;
    if (!el || typeof IntersectionObserver === 'undefined') return undefined;
    // A box that only touches the clipped edge still counts as intersecting: wholly visible or not decides.
    const observer = new IntersectionObserver(([entry]) => setStuck(entry.intersectionRatio < 1), { threshold: [1] });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // Where it ends, for what sticks under it (the open report's header): --ins-sticky-h on the tab's
  // container - its height less the pixel it sits above the edge.
  useEffect(() => {
    const el = bar.current;
    const host = el?.parentElement;
    if (!el || !host || typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(() => host.style.setProperty('--ins-sticky-h', `${el.offsetHeight - 1}px`));
    observer.observe(el);
    return () => { observer.disconnect(); host.style.removeProperty('--ins-sticky-h'); };
  }, []);

  // One row on a wider screen: the views and actions go into the filters' own row.
  const inline = typeof filters === 'function' && !phone;
  const filterRow = typeof filters === 'function' ? filters(inline ? { start: views, end: actions } : {}) : filters;
  const showFilters = !!filters && (!phone || open);
  return (
    <div ref={bar} className={`ins-sticky${stuck ? ' is-stuck' : ''}${phone ? ' is-phone' : ''}`} data-testid={testId}>
      <span ref={edge} className="ins-sticky__edge" aria-hidden="true" />
      {!inline && (views || actions || (phone && filters)) && (
        <div className="ins-sticky__row">
          {views && <div className="ins-sticky__views">{views}</div>}
          <div className="ins-sticky__end">
            {phone && filters && (
              <button type="button" className={`cx-btn cx-btn--sm ins-sticky__filters-btn${open ? ' is-on' : ''}`}
                      aria-expanded={open} onClick={() => setOpen((v) => !v)} data-testid="insights-filters-toggle">
                {open ? <UpOutlined aria-hidden /> : <FilterOutlined aria-hidden />}
                {filterCount > 0
                  ? t('insights.sticky.filtersCount', { count: filterCount })
                  : t('insights.sticky.filters')}
              </button>
            )}
            {actions}
          </div>
        </div>
      )}
      {showFilters && <div className="ins-sticky__filters">{filterRow}</div>}
    </div>
  );
}
